import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';

import GQL from '@lib/cms/gql';
import FlexibleData from '@lib/cms/flexibleData';
import { ALL_COMMUNITIES_QUERY } from '@lib/fragments';

import Container from '@layout/container';
import Grid from '@layout/grid';
import Pagination from '@components/pagination/component';
import FlexiblePage from '@templates/FlexiblePage';
import Select from '@components/select';
import ArticleCard from '@components/article-card';

import { search_bar, state_sort, search, card_outer, zero_state } from './styles.module.scss';

const PAGESIZE = 15;

export async function getStaticProps(context) {
  const preview = context.preview || false;
  const previewEnv = context?.previewData?.environment ?? null;
  const landingData = await FlexibleData.fetch('communities', preview, previewEnv);

  const communityData = await GQL.fetch(ALL_COMMUNITIES_QUERY, { preview }, preview, previewEnv);

  const allCommunities = communityData?.data?.communityCollection?.items ?? [];

  // Create service area object with respective counts of communities
  const serviceAreas = {};
  allCommunities.forEach((comm) => {
    serviceAreas[comm.serviceArea] = (serviceAreas[comm.serviceArea] || 0) + 1;
  });
  // Coerce to array, sort by count, create label/value object
  const serviceAreaOptions = Object.entries(serviceAreas)
    .sort(([, a], [, b]) => b - a)
    .map(([el]) => {
      return { label: el, value: el };
    });

  return {
    props: { allCommunities, landingData, preview, serviceAreaOptions }
  };
}

const CommunityLandingPage = ({ allCommunities, landingData, serviceAreaOptions, preview }) => {
  const router = useRouter();
  const topRef = useRef();
  const [pageNum, setPageNum] = useState(0);
  const [areaValue, setAreaValue] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Filter communities by service Area & 'starts with' input text
  const filteredCommunities = allCommunities.filter((comm) => {
    const serviceAreaMatch = areaValue === 'all' || areaValue === comm.serviceArea;
    const searchTermMatch = comm?.title?.toLowerCase().includes(searchTerm?.toLowerCase());
    return serviceAreaMatch && searchTermMatch;
  });

  // Handle pagination logic
  const total = filteredCommunities.length;
  const startItem = pageNum * PAGESIZE;
  const displayCommunities = filteredCommunities.slice(startItem, startItem + PAGESIZE);

  useEffect(() => {
    if (router?.isReady) {
      const page = router?.query?.page;
      setPageNum(parseInt(page) - 1 || 0);
      const area = router?.query?.area;
      setAreaValue(area || 'all');
    }

    return () => {
      setPageNum();
      setAreaValue();
    };
  }, [router]);

  const handleSelectChange = (newAreaValue) => {
    setAreaValue(newAreaValue);
    setPageNum(0);
    router.push({ pathname: router.pathname, query: { area: newAreaValue, page: 1 } }, undefined, {
      shallow: true
    });
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setPageNum(0);
    router.push({ pathname: router.pathname, query: { ...router.query, page: '1' } }, undefined, {
      shallow: true
    });
  };

  return (
    <FlexiblePage data={landingData} preview={preview}>
      {landingData && (
        <main>
          <Container spacing="large" gridOptions={{ layout: 'single' }}>
            <div ref={topRef} className={search_bar}>
              <div className={state_sort}>
                <Select
                  options={[{ label: 'All', value: 'all' }, ...serviceAreaOptions]}
                  onChange={handleSelectChange}
                  selected={areaValue}
                />
              </div>
              <div className={search}>
                <input
                  tabIndex="0"
                  value={searchTerm}
                  onChange={handleInputChange}
                  placeholder="Search by Community Name"
                />
              </div>
            </div>

            {displayCommunities.length > 0 ? (
              <Grid gutter="20" layout="3">
                {displayCommunities.map((community) => (
                  <div className={card_outer} key={community?.slug}>
                    <ArticleCard
                      description={
                        community?.excerpt || community?.introductionText || community?.summaryText
                      }
                      eyebrow={community.serviceArea.toUpperCase()}
                      image={
                        community?.featuredImage || {
                          height: 960,
                          url: 'https://images.ctfassets.net/wzzep3ntdgx4/4V3cWpoV2LctBsJYc6SnSf/4ef45ecd14a78222b508078d9fe9d345/AdobeStock_376819599.jpeg',
                          width: 1440
                        }
                      }
                      link={{
                        aria: 'Explore this community',
                        icon: true,
                        label: 'Explore',
                        url: `/community/${community?.slug}`
                      }}
                      theme="community"
                      title={community?.title}
                    />
                  </div>
                ))}
              </Grid>
            ) : (
              <div className={zero_state}>
                {areaValue !== 'all'
                  ? 'No communities available under this filter. Try selecting another.'
                  : 'No communities meet your search criteria .'}
              </div>
            )}
          </Container>
          {total > PAGESIZE && (
            <Container spacing="medium" textAlignment="center" gridOptions={{ layout: 'single' }}>
              <Pagination
                items={filteredCommunities}
                per={PAGESIZE}
                currentPage={pageNum}
                scrollToPosition={topRef}
              />
            </Container>
          )}
        </main>
      )}
    </FlexiblePage>
  );
};

export default CommunityLandingPage;
